import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/dist/query/react'
import CONFIG from '../../app-configs'
import { ApiAuthStrategies } from '../shared/ApiAuthStrategies'
import {
  type BillingRatedUsagePersist
} from '../../api-schema-types/asset-holdings/DistributionInstructionPayloadDtos'
import {
  type AssetRatedUsageDetails,
  type BillingRunMonthlyListDto,
  type DistributionInstructionSummaryDetail,
  type InstructionWithCurrencyConversions,
  type MonthlyBillingRunSummaryDetail,
  type PaymentConfirmedDistributionsSummary
} from '../../api-schema-types/asset-holdings/DistributionInstructionDomainDtos'
import { distributionInstructionApiMapper } from './distributionInstructionApiMapper'

export const distributionInstructionApi = createApi({
  reducerPath: 'distributionInstructionApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${CONFIG.PROXIMA_BACK_END_V2_BASE_API_URL}/asset-holdings/distribution-instructions`,
    prepareHeaders: (headers: Headers, { getState }) => {
      const authToken = ApiAuthStrategies.getBearerTokenHeader()
      headers.set('authorization', authToken)
      return headers
    }
  }),

  tagTypes: ['DistributionInstruction', 'BillingRun', 'BillingRuns'],

  endpoints: (builder) => ({
    createBillingRun: builder.mutation({
      query: ({ ...patch }) => ({
        url: '/create-billing-run',
        method: 'POST',
        body: patch
      }),
      invalidatesTags: ['BillingRuns']
    }),
    parseBillingCSVFile: builder.mutation({
      query: ({ ...patch }) => ({
        url: '/validate-billing',
        method: 'POST',
        body: patch
      }),
      invalidatesTags: ['BillingRuns', 'BillingRun']
    }),
    getMonthlyRuns: builder.query({
      query: () => ({
        url: '/billing-runs'
      }),
      providesTags: ['BillingRuns'],
      transformResponse: (response: BillingRunMonthlyListDto[], meta, arg) => distributionInstructionApiMapper().billingRunSummariesMapper(response, meta, arg)
    }),
    getPaymentConfirmedSummary: builder.query({
      query: () => ({
        url: '/pending'
      }),
      providesTags: ['BillingRun'],
      transformResponse: (response: PaymentConfirmedDistributionsSummary, meta, arg) => distributionInstructionApiMapper().paymentConfirmedSummariesMapper(response, meta, arg)
    }),
    getDistributionInstructionsForBillingMonth: builder.query({
      query: ({ ...patch }) => ({
        url: 'all-instructions-for-run',
        method: 'POST',
        body: patch
      }),
      providesTags: ['BillingRun'],
      transformResponse: (response: MonthlyBillingRunSummaryDetail, meta, arg) => distributionInstructionApiMapper().distributionInstructionsMapper(response, meta, arg)
    }),
    getCurrencyConversionsForDistributionInstruction: builder.query({
      query: ({ ...patch }) => ({
        url: '/currency-conversions',
        method: 'POST',
        body: patch
      }),
      providesTags: ['DistributionInstruction'],
      transformResponse: (response: InstructionWithCurrencyConversions[], meta, arg) => distributionInstructionApiMapper().distributionInstructionSummaryMapper(response, meta, arg)
    }),

    confirmInstructionPayment: builder.mutation({
      query: ({ ...patch }) => ({
        url: '/confirm-instruction-payment',
        method: 'POST',
        body: patch
      }),
      invalidatesTags: ['BillingRuns', 'BillingRun', 'DistributionInstruction']
    }),

    cancelDistributionInstruction: builder.mutation({
      query: ({ ...patch }) => ({
        url: '/cancel-instruction',
        method: 'DELETE',
        body: patch
      }),
      invalidatesTags: ['BillingRuns', 'BillingRun', 'DistributionInstruction']
    }),
    setCurrencyConversionExchangeRate: builder.mutation({
      query: ({ ...patch }) => ({
        url: '/conversions-exchange-rate',
        method: 'POST',
        body: patch
      }),
      invalidatesTags: ['BillingRuns', 'BillingRun', 'DistributionInstruction']
    }),
    distributeConfirmedInstructions: builder.mutation({
      query: () => ({
        url: '/distribute-instruction',
        method: 'PUT'
      }),
      invalidatesTags: ['BillingRuns', 'BillingRun', 'DistributionInstruction']
    }),
    generatedRatedUsages: builder.mutation<AssetRatedUsageDetails, Partial<BillingRatedUsagePersist>>({
      query: ({ ...patch }) => ({
        url: 'generate-rated-usages',
        method: 'POST',
        body: patch
      }),
      invalidatesTags: ['BillingRuns', 'BillingRun']
    }),
    getAssetDistributionInstructionDetails: builder.query({
      query: ({ assetDistributionInstructionId }) => ({
        url: '/asset-holder-instructions',
        params: {
          assetDistributionInstructionId
        }
      }),
      providesTags: ['DistributionInstruction'],
      transformResponse: (response: DistributionInstructionSummaryDetail, meta, arg) => distributionInstructionApiMapper().assetHolderDistributionInstructionsMapper(response, meta, arg)
    })
  })
})

export const useDistributionInstructionApiCreateBillingRunMutation = distributionInstructionApi.endpoints.createBillingRun.useMutation
export const useDistributionInstructionApiParseBillingCSVFileMutation = distributionInstructionApi.endpoints.parseBillingCSVFile.useMutation

export const useGetBillingMonthRunsQuery = distributionInstructionApi.endpoints.getMonthlyRuns.useQuery
export const useGetPaymentConfirmedSummaryQuery = distributionInstructionApi.endpoints.getPaymentConfirmedSummary.useQuery
export const useGetAssetDistributionsForBillingMonthQuery = distributionInstructionApi.endpoints.getDistributionInstructionsForBillingMonth.useQuery
export const useGetCurrencyConversionsForDistributionInstructionQuery = distributionInstructionApi.endpoints.getCurrencyConversionsForDistributionInstruction.useQuery
export const useGetAssetDistributionInstructionDetailsQuery = distributionInstructionApi.endpoints.getAssetDistributionInstructionDetails.useQuery
export const useDistributionInstructionApiGeneratedRatedUsagesMutation = distributionInstructionApi.endpoints.generatedRatedUsages.useMutation

export const useConfirmInstructionPaymentMutation = distributionInstructionApi.endpoints.confirmInstructionPayment.useMutation

export const useCancelDistributionInstructionMutation = distributionInstructionApi.endpoints.cancelDistributionInstruction.useMutation
export const useSetCurrencyConversionExchangeRateMutation = distributionInstructionApi.endpoints.setCurrencyConversionExchangeRate.useMutation
export const useDistributeConfirmedInstructionsMutation = distributionInstructionApi.endpoints.distributeConfirmedInstructions.useMutation
